import React, { useState } from "react";
import { Field, Form } from "react-final-form";
import { useDispatch } from "react-redux";

import { createHandTemplate, createHandbookTemplateSection } from "pages/HandBook/model/thunks";

import { useCreateHandbookContext } from "./CreateHandbookContext";
import { useTypedSelector } from "app/store/typedUseSelector";
import Actions from "shared/ui/controls/Actions/Actions";
import TableReusableRow from "shared/ui/dataDisplay/TableReusable/TableReusableRow";
import InputBase from "shared/ui/inputs/InputBase";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { composeFieldValidators, minLength, required } from "utils/formHelpers/validations";

import styles from "./CreateHandbookTemplate.module.scss";

export interface CreateHandbookTemplateProps {
  onSubmit: (vals: { name: string }) => void;
  isPending: boolean;
  nameFieldPlaceholder: string;
  onCancel: () => void;
}

const CreateHanbookTemplateComponent: React.FC<CreateHandbookTemplateProps> = ({
  onSubmit,
  isPending,
  nameFieldPlaceholder,
  onCancel,
}) => {
  return (
    <Form
      onSubmit={isPending ? () => {} : onSubmit}
      render={({ form }) => (
        <form onSubmit={(e) => e.preventDefault()} autoComplete="off">
          <TableReusableRow isHoverable={false} containerClassName={styles.row}>
            <RowGrid percents={[95, 5]}>
              <Field
                name="name"
                component={InputBase}
                placeholder={nameFieldPlaceholder}
                onEnter={() => {
                  form.submit();
                }}
                validate={composeFieldValidators(required(), minLength(1))}
              />
              <Actions
                canSave
                canRemove
                onRemoveDirectly={onCancel}
                onSave={() => {
                  form.submit();
                }}
                isPending={isPending}
              />
            </RowGrid>
          </TableReusableRow>
        </form>
      )}
    />
  );
};

const withCreateTemplate = (Base: React.ComponentType<CreateHandbookTemplateProps>) => () => {
  const { setIsSectionCreating, isSectionCreating } = useCreateHandbookContext();
  const isPending = useTypedSelector((state) => state.handBookReducer.loadings)["createTemplate"];
  const dispatch = useDispatch();

  const create = (vals: any) => {
    dispatch(createHandTemplate(vals, () => setIsSectionCreating(false)));
  };

  if (!isSectionCreating) return null;

  return (
    <Base
      isPending={isPending}
      onSubmit={create}
      nameFieldPlaceholder="Введите название шаблона"
      onCancel={() => setIsSectionCreating(false)}
    />
  );
};

const withCreateTemplateSection =
  (Base: React.ComponentType<CreateHandbookTemplateProps>) =>
  ({
    templateId,
    sectionId,
    nameFieldPlaceholder,
  }: {
    templateId?: string;
    sectionId?: string;
    nameFieldPlaceholder: string;
  }) => {
    const { setIsSectionCreating, isSectionCreating } = useCreateHandbookContext();
    const isPending = useTypedSelector((state) => state.handBookReducer.loadings)["createTemplateSection"];
    const dispatch = useDispatch();

    const create = (vals: any) => {
      const data = { ...vals };
      if (templateId) {
        data.template = templateId;
      }
      if (sectionId) {
        data.parent = sectionId;
      }
      dispatch(createHandbookTemplateSection(data, () => setIsSectionCreating(false)));
    };

    if (!isSectionCreating) return null;

    return (
      <Base
        isPending={isPending}
        onSubmit={create}
        nameFieldPlaceholder={nameFieldPlaceholder}
        onCancel={() => setIsSectionCreating(false)}
      />
    );
  };

export const CreateHandbookTemplate = withCreateTemplate(CreateHanbookTemplateComponent);
export const CreateHandbookTemplateSection = withCreateTemplateSection(CreateHanbookTemplateComponent);
